import { useState } from 'react';

import {
  ActionIcon,
  Button,
  Code,
  Combobox,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';

import { FilterApplication, VariableBlock } from '@/types';
import { VariableOption } from '../InlineEditor';

interface VariableBlockViewProps {
  block: VariableBlock;
  onChange: (next: VariableBlock) => void;
  variables: VariableOption[];
  filterOptions: { name: string; description: string }[];
}

export function VariableBlockView({
  block,
  onChange,
  variables,
  filterOptions,
}: Readonly<VariableBlockViewProps>) {
  const [addingFilter, setAddingFilter] = useState<string | null>(null);
  const [argDraft, setArgDraft] = useState('');
  const filterCombobox = useCombobox();

  const data = variables.map((v) => ({
    value: v.path,
    label: `${v.label} — ${v.path}`,
  }));

  const removeFilter = (idx: number) => {
    const next = block.filters.filter((_, i) => i !== idx);
    onChange({ ...block, filters: next });
  };

  const beginAdd = (name: string) => {
    setAddingFilter(name);
    setArgDraft('');
    filterCombobox.closeDropdown();
  };

  const confirmAdd = () => {
    if (!addingFilter) {
      return;
    }
    const newFilter: FilterApplication = {
      name: addingFilter,
      args: argDraft.trim() ? [argDraft] : [],
    };
    onChange({ ...block, filters: [...block.filters, newFilter] });
    setAddingFilter(null);
    setArgDraft('');
  };

  return (
    <Stack gap="xs">
      <Select
        size="sm"
        label="Variable"
        data={data}
        value={block.path || null}
        onChange={(value) => {
          if (value) {
            onChange({ ...block, path: value });
          }
        }}
        allowDeselect={false}
        searchable
        placeholder="Pick a variable"
      />

      {block.filters.length > 0 && (
        <Group gap={4}>
          {block.filters.map((f, idx) => (
            <Group
              key={`${f.name}-${idx}`}
              gap={2}
              wrap="nowrap"
              style={{
                background: 'var(--mantine-color-default-hover)',
                borderRadius: 4,
                padding: '2px 4px 2px 8px',
              }}
            >
              <Code style={{ background: 'transparent', fontSize: 12 }}>
                {f.args.length ? `| ${f.name}(${f.args.join(', ')})` : `| ${f.name}`}
              </Code>
              <ActionIcon
                size="xs"
                variant="subtle"
                onClick={() => removeFilter(idx)}
                aria-label="Remove filter"
              >
                <IconX size={12} />
              </ActionIcon>
            </Group>
          ))}
        </Group>
      )}

      {addingFilter ? (
        <Group gap="xs">
          <Text size="xs">| {addingFilter}(</Text>
          <TextInput
            size="xs"
            placeholder="argument (optional)"
            value={argDraft}
            onChange={(e) => setArgDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmAdd();
              }
            }}
            style={{ flex: 1 }}
            autoFocus
          />
          <Text size="xs">)</Text>
          <Button size="xs" onClick={confirmAdd}>
            Add
          </Button>
          <Button
            size="xs"
            variant="subtle"
            onClick={() => {
              setAddingFilter(null);
              setArgDraft('');
            }}
          >
            Cancel
          </Button>
        </Group>
      ) : (
        <Combobox
          store={filterCombobox}
          onOptionSubmit={(value) => beginAdd(value)}
          withinPortal={false}
        >
          <Combobox.Target>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconPlus size={12} />}
              onClick={() => filterCombobox.toggleDropdown()}
            >
              Add filter
            </Button>
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options>
              {filterOptions.length === 0 ? (
                <Text c="dimmed" size="xs" px="xs" py="md">
                  No filters available
                </Text>
              ) : (
                filterOptions.map((f) => (
                  <Combobox.Option key={f.name} value={f.name}>
                    <Stack gap={0}>
                      <Text size="sm">{f.name}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {f.description}
                      </Text>
                    </Stack>
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      )}
    </Stack>
  );
}
